import { coreClient, apiRequest } from '../client.js';
import type { JsonValue, HealthStatus } from '../types.js';

export interface PKBPromptEntry {
  promptType: string;
  text: string;
  isDefault?: boolean;
  updatedBy?: string;
  updatedAt?: string;
}

export interface PKBConfig {
  sensitivity?: 'strict' | 'moderate' | 'permissive';
  enableImageEventLogging?: boolean;
  [key: string]: JsonValue | undefined;
}

export interface PKBImageEvent {
  messageId: string;
  channelId: string;
  guildId: string;
  userId: string;
  imageUrl: string;
  analysis?: JsonValue;
  botResponse?: string;
  createdAt: string;
}

export interface PKBUserPrompt {
  userId: string;
  text: string | null;
  isEnabled: boolean;
  hasCustomPrompt: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  history?: Array<{ text: string; createdAt: string | null }>;
}

export interface PKBStats {
  totalConversations: number;
  totalImageEvents: number;
  activeGuilds: number;
  status: HealthStatus;
  timestamp: string;
}

export const praiseKinkApi = {
  getStats: () =>
    apiRequest<PKBStats>(coreClient, {
      method: 'GET',
      url: '/praise-kink/stats',
    }),

  getPrompts: () =>
    apiRequest<Record<string, PKBPromptEntry>>(coreClient, {
      method: 'GET',
      url: '/praise-kink/prompts',
    }),

  getPrompt: (promptType: string) =>
    apiRequest<PKBPromptEntry>(coreClient, {
      method: 'GET',
      url: `/praise-kink/prompts/${encodeURIComponent(promptType)}`,
    }),

  setPrompt: (promptType: string, text: string, updatedBy?: string) =>
    apiRequest(coreClient, {
      method: 'PUT',
      url: `/praise-kink/prompts/${encodeURIComponent(promptType)}`,
      data: { text, updatedBy },
    }),

  resetPrompt: (promptType: string) =>
    apiRequest(coreClient, {
      method: 'DELETE',
      url: `/praise-kink/prompts/${encodeURIComponent(promptType)}`,
    }),

  resetAllPrompts: () =>
    apiRequest(coreClient, {
      method: 'POST',
      url: '/praise-kink/prompts/reset-all',
    }),

  getConfig: () =>
    apiRequest<PKBConfig>(coreClient, {
      method: 'GET',
      url: '/praise-kink/config',
    }),

  setConfig: (key: string, value: JsonValue, updatedBy?: string) =>
    apiRequest(coreClient, {
      method: 'POST',
      url: `/praise-kink/config/${encodeURIComponent(key)}`,
      data: { value, updatedBy },
    }),

  listImageEvents: (limit = 20, offset = 0) =>
    apiRequest<PKBImageEvent[]>(coreClient, {
      method: 'GET',
      url: '/praise-kink/image-events',
      params: { limit, offset },
    }),

  getImageEvent: (messageId: string) =>
    apiRequest<PKBImageEvent>(coreClient, {
      method: 'GET',
      url: `/praise-kink/image-events/${encodeURIComponent(messageId)}`,
    }),

  deleteImageEvent: (messageId: string) =>
    apiRequest(coreClient, {
      method: 'DELETE',
      url: `/praise-kink/image-events/${encodeURIComponent(messageId)}`,
    }),

  getUserPrompt: (userId: string) =>
    apiRequest<PKBUserPrompt>(coreClient, {
      method: 'GET',
      url: `/praise-kink/user-prompts/${encodeURIComponent(userId)}`,
    }),
  listUserPrompts: () =>
    apiRequest<{ prompts: PKBUserPrompt[] }>(coreClient, {
      method: 'GET',
      url: '/praise-kink/user-prompts',
    }),
  setUserPrompt: (userId: string, text?: string, enabled?: boolean) =>
    apiRequest(coreClient, {
      method: 'PUT',
      url: `/praise-kink/user-prompts/${encodeURIComponent(userId)}`,
      data: { text, enabled },
    }),
  deleteUserPrompt: (userId: string) =>
    apiRequest(coreClient, {
      method: 'DELETE',
      url: `/praise-kink/user-prompts/${encodeURIComponent(userId)}`,
    }),
};

export default praiseKinkApi;
